import { useIsAuthenticated } from "#hooks/use-auth"
import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import i18n from "#lib/i18n"
import { queryKeys as globalQueryKeys } from "#lib/query-keys"
import { homeFinancialSDK } from "#sdk/sdk"
import { getDefaultCurrencyForLocale } from "@home-finance-manager/shared"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"

const queryKeys = {
  all: ["user-settings"] as const,
  settings: () => [...queryKeys.all, "settings"] as const,
}

export interface UserSettings {
  id: number
  userId: number
  locale: string
  preferences: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

/**
 * Hook to get user settings
 */
export function useUserSettings() {
  const isAuthenticated = useIsAuthenticated()

  return useQuery({
    queryKey: queryKeys.settings(),
    queryFn: async () => {
      const response = await homeFinancialSDK.users.getSettings()
      return response.data
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to update user locale
 */
export function useUpdateUserLocale() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (locale: string) => {
      const response = await homeFinancialSDK.users.updateLocale(locale)
      return response.data
    },
    onSuccess: async (data, locale) => {
      // Update the query cache first
      queryClient.setQueryData(queryKeys.settings(), data)

      // Update i18n language - this will trigger re-renders through react-i18next
      // Force a change by setting language and emitting the languageChanged event
      await i18n.changeLanguage(locale)
      localStorage.setItem("i18nextLng", locale)

      // Invalidate dashboard analytics queries since they contain localized month labels
      // This ensures the analytics data is refetched with the new locale
      queryClient.invalidateQueries({ queryKey: globalQueryKeys.dashboard.all })
    },
  })
}

/**
 * Hook to update user preferences
 */
export function useUpdateUserPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (preferences: Record<string, unknown>) => {
      const response = await homeFinancialSDK.users.updatePreferences(preferences)
      return response.data
    },
    onSuccess: (data) => {
      // Update the query cache
      queryClient.setQueryData(queryKeys.settings(), data)
    },
  })
}

/**
 * Hook to get user currency preference with locale fallback
 */
export function useUserCurrency(): string {
  const { data: settings } = useUserSettings()
  const currentLocale = i18n.language || "en"

  return useMemo(() => {
    // If user is authenticated and has currency preference, use it
    if (settings?.preferences?.currency && typeof settings.preferences.currency === "string") {
      return settings.preferences.currency
    }

    // Fallback to locale-based default
    // If authenticated, use user's locale from settings, otherwise use i18n language
    const locale = settings?.locale || currentLocale
    return getDefaultCurrencyForLocale(locale)
  }, [settings, currentLocale])
}

/**
 * Hook to update user currency preference
 */
export function useUpdateUserCurrency() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (currency: string) => {
      const response = await homeFinancialSDK.users.updateCurrency(currency)
      return response.data
    },
    onSuccess: (data) => {
      // Update the query cache
      queryClient.setQueryData(queryKeys.settings(), data)

      // Invalidate dashboard analytics queries since they contain currency-formatted amounts
      queryClient.invalidateQueries({ queryKey: globalQueryKeys.dashboard.all })
    },
  })
}
